import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Home, 
  User, 
  Shield,
  Banknote,
  AlertCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import Header from '../components/Header';
import { CommissionFeeBreakdown } from '../components/CommissionFeeBreakdown';
import { parseCommission, formatGBP } from '../lib/commissionPricing';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cashflow-596/artifacts/ipba3h4w_Gazeal%20Advance%20logo.png";
const POWERED_BY_LOGO_URL = "/powered-by-agents-capital.png";

const GetPaidPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    // Step 1: Agent Details
    agentName: '',
    agentEmail: '',
    agentPhone: '',
    agencyName: '',
    // Step 2: Property Details
    propertyAddress: '',
    propertyPostcode: '',
    salePrice: '',
    commissionAmount: '',
    // Step 3: Reservation Status
    hasReservation: null,
    reservationNumber: '',
    // If no reservation
    sellerName: '',
    sellerEmail: '',
    sellerPhone: '',
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    // Step 4: Withdrawal Amount
    withdrawalAmount: null,
    // Step 5: Confirmation
    acceptTerms: false,
  });

  const totalSteps = 5;
  const commissionAmount = parseCommission(formData.commissionAmount) ?? 0;
  const withdrawalAmount = formData.withdrawalAmount ?? commissionAmount;

  const steps = [
    { number: 1, title: 'Agent Details', icon: User },
    { number: 2, title: 'Property', icon: Home },
    { number: 3, title: 'Reservation', icon: Shield },
    { number: 4, title: 'Amount', icon: Banknote },
    { number: 5, title: 'Confirm', icon: Check },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'commissionAmount' ? { withdrawalAmount: null } : {}),
    }));
    // Clear validation error when user types
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 1:
        if (!formData.agentName.trim()) errors.agentName = 'Full name is required';
        if (!formData.agentEmail.trim()) errors.agentEmail = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.agentEmail)) errors.agentEmail = 'Valid email is required';
        if (!formData.agentPhone.trim()) errors.agentPhone = 'Phone number is required';
        if (!formData.agencyName.trim()) errors.agencyName = 'Agency name is required';
        break;
      case 2:
        if (!formData.propertyAddress.trim()) errors.propertyAddress = 'Property address is required';
        if (!formData.propertyPostcode.trim()) errors.propertyPostcode = 'Postcode is required';
        if (!formData.salePrice.trim()) errors.salePrice = 'Sale price is required';
        if (!formData.commissionAmount.trim()) errors.commissionAmount = 'Commission amount is required';
        else if (parseCommission(formData.commissionAmount) === null) errors.commissionAmount = 'Enter a valid amount between £0.01 and £999,999,999.99, with at most two decimal places';
        break;
      case 3:
        if (formData.hasReservation === null) errors.hasReservation = 'Please select an option';
        break;
      case 4:
        if (withdrawalAmount <= 0 || withdrawalAmount > commissionAmount) {
          errors.withdrawalAmount = 'Choose a withdrawal greater than £0 and no more than your commission';
        }
        break;
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.acceptTerms || !validateStep(4)) return;
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/get-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, withdrawalAmount }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map((error) => error.msg).join('. ') : data.detail;
        throw new Error(detail || data.message || 'Failed to submit request');
      }
      
      alert(data.message || 'Application submitted successfully! Our team will contact you as soon as possible.');
      navigate('/');
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Your Details</h3>
              <p className="text-white/60">Tell us about yourself and your agency.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Your Full Name *</label>
                <input
                  type="text"
                  value={formData.agentName}
                  data-testid="commission-agent-name"
                  onChange={(e) => handleInputChange('agentName', e.target.value)}
                  className={`custom-input ${validationErrors.agentName ? 'border-red-500' : ''}`}
                  placeholder="John Smith"
                  required
                />
                {validationErrors.agentName && <p data-testid="commission-agent-name-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.agentName}</p>}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Agency Name *</label>
                <input
                  type="text"
                  value={formData.agencyName}
                  data-testid="commission-agency-name"
                  onChange={(e) => handleInputChange('agencyName', e.target.value)}
                  className={`custom-input ${validationErrors.agencyName ? 'border-red-500' : ''}`}
                  placeholder="Smith & Partners Estate Agents"
                  required
                />
                {validationErrors.agencyName && <p data-testid="commission-agency-name-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.agencyName}</p>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.agentEmail}
                  data-testid="commission-agent-email"
                  onChange={(e) => handleInputChange('agentEmail', e.target.value)}
                  className={`custom-input ${validationErrors.agentEmail ? 'border-red-500' : ''}`}
                  placeholder="john@smithpartners.co.uk"
                  required
                />
                {validationErrors.agentEmail && <p data-testid="commission-agent-email-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.agentEmail}</p>}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.agentPhone}
                  data-testid="commission-agent-phone"
                  onChange={(e) => handleInputChange('agentPhone', e.target.value)}
                  className={`custom-input ${validationErrors.agentPhone ? 'border-red-500' : ''}`}
                  placeholder="+44 7700 900000"
                  required
                />
                {validationErrors.agentPhone && <p data-testid="commission-agent-phone-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.agentPhone}</p>}
              </div>
            </div>
            
            {/* Partnership Box */}
            <div className="mt-8 p-6 bg-gradient-to-r from-[#59d862]/10 to-transparent border border-[#59d862]/30 rounded-2xl">
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                <img
                  src={POWERED_BY_LOGO_URL}
                  alt="Powered by AgentsCapital"
                  className="h-8 md:h-10 w-auto opacity-90"
                />
                <p className="text-white/70 text-sm md:text-base text-center md:text-left leading-relaxed">
                  Gazeal Advance is built on <span className="text-[#59d862] font-medium">AgentsCapital's proven process</span> which is trusted by agents across the UK.{' '}
                  <a 
                    data-testid="commission-partner-learn-more"
                    href="https://agentscapital.co" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#59d862] hover:text-[#6de876] inline-flex items-center gap-1 font-medium"
                  >
                    Learn more
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Property Details</h3>
              <p className="text-white/60">Tell us about the property you want to access commission for.</p>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Property Address *</label>
              <input
                type="text"
                value={formData.propertyAddress}
                data-testid="commission-property-address"
                onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
                className={`custom-input ${validationErrors.propertyAddress ? 'border-red-500' : ''}`}
                placeholder="123 High Street, London"
                required
              />
              {validationErrors.propertyAddress && <p data-testid="commission-property-address-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.propertyAddress}</p>}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Postcode *</label>
                <input
                  type="text"
                  value={formData.propertyPostcode}
                  data-testid="commission-property-postcode"
                  onChange={(e) => handleInputChange('propertyPostcode', e.target.value)}
                  className={`custom-input ${validationErrors.propertyPostcode ? 'border-red-500' : ''}`}
                  placeholder="SW1A 1AA"
                  required
                />
                {validationErrors.propertyPostcode && <p data-testid="commission-property-postcode-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.propertyPostcode}</p>}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Sale Price *</label>
                <input
                  type="text"
                  value={formData.salePrice}
                  data-testid="commission-sale-price"
                  onChange={(e) => handleInputChange('salePrice', e.target.value)}
                  className={`custom-input ${validationErrors.salePrice ? 'border-red-500' : ''}`}
                  placeholder="£450,000"
                  required
                />
                {validationErrors.salePrice && <p data-testid="commission-sale-price-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.salePrice}</p>}
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Your Commission Amount *</label>
              <input
                type="text"
                value={formData.commissionAmount}
                data-testid="commission-total-amount"
                inputMode="decimal"
                onChange={(e) => handleInputChange('commissionAmount', e.target.value)}
                className={`custom-input ${validationErrors.commissionAmount ? 'border-red-500' : ''}`}
                placeholder="£5,400"
                required
              />
              {validationErrors.commissionAmount && <p data-testid="commission-total-amount-error" role="alert" className="text-red-400 text-xs mt-1">{validationErrors.commissionAmount}</p>}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Reservation Agreement Status</h3>
              <p className="text-white/60">Do you have a Gazeal Reservation Agreement in place for this sale?</p>
            </div>

            {validationErrors.hasReservation && <p data-testid="commission-reservation-error" role="alert" className="text-red-400 text-sm">{validationErrors.hasReservation}</p>}

            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleInputChange('hasReservation', true)}
                data-testid="commission-reservation-yes"
                aria-pressed={formData.hasReservation === true}
                className={`p-6 border text-left transition-all duration-300 rounded-2xl ${
                  formData.hasReservation === true
                    ? 'border-[#59d862] bg-[#59d862]/10'
                    : 'border-white/20 bg-white/5 hover:border-white/40'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.hasReservation === true ? 'bg-[#59d862]' : 'bg-white/10'
                  }`}>
                    <Check className={`w-6 h-6 ${
                      formData.hasReservation === true ? 'text-black' : 'text-white/60'
                    }`} />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Yes, I have one</div>
                    <div className="text-white/50 text-sm">Ready to access commission</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleInputChange('hasReservation', false)}
                data-testid="commission-reservation-no"
                aria-pressed={formData.hasReservation === false}
                className={`p-6 border text-left transition-all duration-300 rounded-2xl ${
                  formData.hasReservation === false
                    ? 'border-[#c4b9a6] bg-[#c4b9a6]/10'
                    : 'border-white/20 bg-white/5 hover:border-white/40'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.hasReservation === false ? 'bg-[#c4b9a6]' : 'bg-white/10'
                  }`}>
                    <AlertCircle className={`w-6 h-6 ${
                      formData.hasReservation === false ? 'text-black' : 'text-white/60'
                    }`} />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Not yet</div>
                    <div className="text-white/50 text-sm">We'll help you set one up</div>
                  </div>
                </div>
              </button>
            </div>

            {formData.hasReservation === true && (
              <div data-testid="commission-reservation-confirmation" className="animate-slide-up p-4 bg-[#59d862]/10 border border-[#59d862]/30 rounded-xl">
                <p className="text-white/70 text-sm">
                  <span className="text-[#59d862] font-medium">Great!</span> We'll verify the reservation agreement using the property address you've provided. You're ready to proceed.
                </p>
              </div>
            )}

            {formData.hasReservation === false && (
              <div className="space-y-6 animate-slide-up p-6 bg-[#004048]/30 border border-[#59d862]/30 rounded-2xl">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-[#59d862] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Set Up a Reservation Agreement</h4>
                    <p className="text-white/60 text-sm">
                      Provide seller and buyer details below. Our specialists will contact them to protect your sale with a Gazeal Reservation Agreement, enabling you to access your commission early.
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h5 className="text-[#c4b9a6] font-medium">Seller Details</h5>
                    <input
                      type="text"
                      value={formData.sellerName}
                      data-testid="commission-seller-name"
                      onChange={(e) => handleInputChange('sellerName', e.target.value)}
                      className="custom-input"
                      placeholder="Seller Name"
                    />
                    <input
                      type="email"
                      value={formData.sellerEmail}
                      data-testid="commission-seller-email"
                      onChange={(e) => handleInputChange('sellerEmail', e.target.value)}
                      className="custom-input"
                      placeholder="Seller Email"
                    />
                    <input
                      type="tel"
                      value={formData.sellerPhone}
                      data-testid="commission-seller-phone"
                      onChange={(e) => handleInputChange('sellerPhone', e.target.value)}
                      className="custom-input"
                      placeholder="Seller Phone"
                    />
                  </div>
                  <div className="space-y-4">
                    <h5 className="text-[#59d862] font-medium">Buyer Details</h5>
                    <input
                      type="text"
                      value={formData.buyerName}
                      data-testid="commission-buyer-name"
                      onChange={(e) => handleInputChange('buyerName', e.target.value)}
                      className="custom-input"
                      placeholder="Buyer Name"
                    />
                    <input
                      type="email"
                      value={formData.buyerEmail}
                      data-testid="commission-buyer-email"
                      onChange={(e) => handleInputChange('buyerEmail', e.target.value)}
                      className="custom-input"
                      placeholder="Buyer Email"
                    />
                    <input
                      type="tel"
                      value={formData.buyerPhone}
                      data-testid="commission-buyer-phone"
                      onChange={(e) => handleInputChange('buyerPhone', e.target.value)}
                      className="custom-input"
                      placeholder="Buyer Phone"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        const maxCommission = commissionAmount;
        const sliderValue = withdrawalAmount;
        const percentage = maxCommission > 0 ? Math.round((sliderValue / maxCommission) * 100) : 100;
        
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Withdrawal Amount</h3>
              <p className="text-white/60">Choose how much of your commission you'd like to access now.</p>
            </div>

            {formData.commissionAmount && (
              <div className="p-6 bg-[#004048]/30 border border-[#59d862]/30 rounded-2xl">
                <div className="text-white/60 text-sm mb-1">Your total commission</div>
                <div data-testid="withdrawal-total-commission" className="text-2xl sm:text-3xl font-bold text-[#59d862] break-all">{formatGBP(maxCommission)}</div>
              </div>
            )}

            {maxCommission > 0 ? (
              <div className="space-y-6">
                <div className="text-center py-6">
                  <div className="text-white/50 text-sm mb-2">Amount to withdraw</div>
                  <div data-testid="withdrawal-selected-amount" className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-2 heading break-all">
                    {formatGBP(sliderValue)}
                  </div>
                  <div 
                    data-testid="withdrawal-percentage"
                    className="inline-block px-4 py-1 text-lg font-semibold transition-all duration-300"
                    style={{
                      background: `linear-gradient(90deg, rgba(89, 216, 98, ${percentage / 100}) 0%, rgba(89, 216, 98, ${percentage / 200}) 100%)`,
                      color: percentage > 50 ? '#000' : '#59d862',
                      boxShadow: `0 0 ${percentage / 3}px rgba(89, 216, 98, ${percentage / 100})`
                    }}
                  >
                    {percentage}% of commission
                  </div>
                </div>

                <div className="px-2">
                  <div className="relative">
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-150"
                        style={{
                          width: `${percentage}%`,
                          background: 'linear-gradient(90deg, #59d862 0%, #7ee085 100%)',
                          boxShadow: `0 0 20px rgba(89, 216, 98, ${0.3 + (percentage / 200)}), 0 0 40px rgba(89, 216, 98, ${percentage / 400})`
                        }}
                      />
                    </div>
                    
                    <input
                      data-testid="withdrawal-slider"
                      aria-label="Commission to withdraw"
                      type="range"
                      min="0.01"
                      max={maxCommission}
                      step="0.01"
                      value={sliderValue}
                      onChange={(e) => handleInputChange('withdrawalAmount', parseFloat(e.target.value))}
                      className="absolute inset-0 w-full h-3 opacity-0 cursor-pointer"
                      style={{ margin: 0 }}
                    />
                    
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-[#59d862] rounded-full border-4 border-black pointer-events-none transition-all duration-150"
                      style={{
                        left: `calc(${percentage}% - 12px)`,
                        boxShadow: `0 0 15px rgba(89, 216, 98, 0.8), 0 0 30px rgba(89, 216, 98, 0.4)`
                      }}
                    />
                  </div>

                  <div className="flex justify-between mt-3 text-sm">
                    <span className="text-white/40">£0.01</span>
                    <span data-testid="withdrawal-maximum" className="text-white/40">{formatGBP(maxCommission)}</span>
                  </div>
                </div>

                <div className="flex gap-2 justify-center flex-wrap">
                  {[25, 50, 75, 100].map((pct) => (
                    <button
                      key={pct}
                      data-testid={`withdrawal-preset-${pct}`}
                      type="button"
                      onClick={() => handleInputChange('withdrawalAmount', Math.max(1, Math.round(Math.round(maxCommission * 100) * pct / 100)) / 100)}
                      className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-full ${
                        percentage === pct
                          ? 'bg-[#59d862] text-black'
                          : 'bg-white/5 text-white/70 border border-white/20 hover:border-[#59d862]/50 hover:text-white'
                      }`}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
                
                {/* Fee Calculation Display */}
                {sliderValue > 0 && (
                  <div className="mt-6 p-5 bg-black/40 border border-white/10 rounded-xl space-y-3">
                    <CommissionFeeBreakdown amount={sliderValue} testId="withdrawal" />
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 bg-red-500/10 border border-red-500/30 text-center rounded-xl">
                <p data-testid="withdrawal-commission-error" className="text-red-400">Please enter your commission amount in Step 2 to continue.</p>
              </div>
            )}

            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
              <p className="text-white/60 text-sm">
                <strong className="text-white">Your choice, your control.</strong> You decide which properties and how much commission to withdraw. There's no obligation to access early payment on every sale.
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Confirm Your Application</h3>
              <p className="text-white/60">Review your details and submit your commission advance request.</p>
            </div>

            <div className="space-y-4">
              <div data-testid="commission-confirmation" className="p-4 sm:p-6 bg-white/5 border border-white/10 rounded-2xl [&_.summary-value]:text-right [&_.summary-value]:max-w-[65%] [&_.summary-value]:break-words">
                <h4 className="text-white/60 text-sm mb-4">SUMMARY</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/60">Agent</span>
                    <span data-testid="confirmation-agent" className="text-white summary-value">{formData.agentName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Agency</span>
                    <span data-testid="confirmation-agency" className="text-white summary-value">{formData.agencyName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Property</span>
                    <span data-testid="confirmation-property" className="text-white summary-value">{formData.propertyAddress}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Sale Price</span>
                    <span data-testid="confirmation-sale-price" className="text-white summary-value">{formData.salePrice}</span>
                  </div>
                  <div className="border-t border-white/10 my-4" />
                  <div className="flex justify-between">
                    <span className="text-white/60">Commission</span>
                    <span data-testid="confirmation-commission" className="text-white font-semibold summary-value">{formatGBP(commissionAmount)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <CommissionFeeBreakdown amount={withdrawalAmount} testId="confirmation" />
                  </div>
                </div>
              </div>
            </div>

            {/* AgentsCapital Notice */}
            <div className="p-6 bg-gradient-to-br from-[#59d862]/10 to-[#004048]/20 border border-[#59d862]/30 rounded-2xl mb-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#59d862]/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-[#59d862]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Partnership with AgentsCapital</h4>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">
                    Your information will be securely shared with{' '}
                    <a 
                      data-testid="commission-confirmation-partner-link"
                      href="https://agentscapital.co" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#59d862] hover:underline font-medium"
                    >
                      AgentsCapital
                    </a>
                    {' '}for processing your commission advance request.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-white/60 text-sm">
                      <Check className="w-4 h-4 text-[#59d862] flex-shrink-0 mt-0.5" />
                      <span>We'll review your application</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/60 text-sm">
                      <Check className="w-4 h-4 text-[#59d862] flex-shrink-0 mt-0.5" />
                      <span>You'll receive approval confirmation via email</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/60 text-sm">
                      <Check className="w-4 h-4 text-[#59d862] flex-shrink-0 mt-0.5" />
                      <span>No fees until you use the service</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#004048]/30 border border-[#59d862]/30 rounded-2xl">
              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="commission-accept-terms"
                  checked={formData.acceptTerms}
                  onChange={(event) => handleInputChange('acceptTerms', event.target.checked)}
                  className="w-6 h-6 flex-shrink-0 mt-0.5 accent-[#59d862] cursor-pointer"
                />
                <span className="text-white/80 text-sm">
                  I confirm that the information provided is accurate and I agree to the Gazeal Advance terms and conditions. I understand that a Gazeal Reservation Agreement is required to access commission early.
                </span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <Link data-testid="commission-back-home" to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get Paid <span className="text-[#59d862]">Now</span>
            </h1>
            <p className="text-white/60">
              Access your commission within hours of your sale being secured.
            </p>
          </div>

          <div className="flex items-center justify-between mb-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      data-testid={`commission-step-${step.number}`}
                      aria-current={isActive ? 'step' : undefined}
                      className={`step-indicator ${
                        isActive ? 'active' : isCompleted ? 'completed' : ''
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5 text-[#59d862]" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isActive ? 'text-black' : 'text-white/60'}`} />
                      )}
                    </div>
                    <span className={`text-xs mt-2 hidden md:block ${
                      isActive ? 'text-[#59d862]' : 'text-white/40'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      currentStep > step.number ? 'bg-[#59d862]' : 'bg-white/20'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="glass-card p-4 sm:p-8 md:p-12">
            {renderStep()}

            <div className="flex flex-col gap-4 mt-10 pt-8 border-t border-white/10">
              {validationErrors.withdrawalAmount && (
                <p data-testid="withdrawal-validation-error" role="alert" className="text-red-400 text-sm">{validationErrors.withdrawalAmount}</p>
              )}
              {submitError && (
                <div data-testid="commission-submit-error" role="alert" className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                  <p className="text-red-400 text-sm">{submitError}</p>
                </div>
              )}
              
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <button
                  onClick={prevStep}
                  data-testid="commission-back-button"
                  disabled={currentStep === 1 || isSubmitting}
                  className={`flex items-center gap-2 px-6 py-3 transition-all duration-300 ${
                    currentStep === 1 || isSubmitting
                      ? 'text-white/30 cursor-not-allowed'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>

                {currentStep < totalSteps ? (
                  <button
                    onClick={nextStep}
                    data-testid="commission-continue-button"
                    className="btn-primary px-8 py-3"
                  >
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    data-testid="commission-submit-button"
                    disabled={!formData.acceptTerms || isSubmitting}
                    className={`btn-primary px-8 py-3 ${
                      !formData.acceptTerms || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <Check className="w-5 h-5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-black border-t border-white/10 py-8">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="Gazeal Advance" className="h-8 mx-auto mb-4" />
          <p className="text-white/40 text-sm">© {new Date().getFullYear()} Gazeal Advance. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default GetPaidPage;
